import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "hooks/useHistory";
import { webAppBackHandler } from "@Utils/webApputils";
import { WEB_VIEW_APP_EVENTS } from "@Utils/webApputils/analyticEvents";
import { initWebViewEventListener, subscribeToWebViewEvent } from "@Utils/webviewEvents";
import { authMiddlewareClient } from "@App/appReducer";
import {
  toggleShowWidgetState,
  setOpenAmberCopilot,
} from "@Pages/PropertyPage/components/ZendeskChat/ChatBotReducer";

export function useWebViewEvents() {
  const dispatch = useDispatch();
  const history = useHistory();

  const singularDeeplinkHandler = (payload: { url: string }) => {
    if (payload?.url || payload?.url === "") {
      const targetUrl = `/${payload.url}`;
      history.push(targetUrl);
    } else {
      console.log("[WebView] No URL in payload, skipping navigation");
    }
  };
  const fcmTokenUpdatedHandler = (payload: { token: string }) => {
    if (payload?.token) {
      window.fcmToken = payload.token;
    }
  };

  const backActionHandler = () => {
    webAppBackHandler(history);
  };

  const userAuthHandler = (payload: { authToken: string }) => {
    window.localStorage.setItem("user_auth_token", payload?.authToken);
    dispatch(authMiddlewareClient(true));
  };

  // The app shell cannot reliably clear our storage from outside the page: removing a
  // `WKWebsiteDataStore` record races a live content process that still holds localStorage in
  // memory. Clearing it here, inside the page's own context, is authoritative.
  const userLoggedOutHandler = () => {
    window.localStorage.removeItem("user_auth_token");
    dispatch(authMiddlewareClient(false));
  };
  // `Chatbot` picks this up, opens itself and resets the flag.
  const openAIChatHandler = () => {
    dispatch(setOpenAmberCopilot(true));
  };

  const navigateToRouteHandler = (payload: { path: string }) => {
    if (payload?.path) {
      history.push(payload.path);
    }
  };

  const handleZendeskWidget = (payload: { isOpened: boolean }) => {
    if (!payload?.isOpened) {
      dispatch(toggleShowWidgetState(false));
      dispatch(setOpenAmberCopilot(false));
      // @ts-ignore
      if (typeof zE === "function") {
        // @ts-ignore
        zE("messenger", "close");
      }

      // @ts-ignore
      if (typeof wn !== "undefined" && typeof wn.hideWidget === "function") {
        // @ts-ignore
        wn.hideWidget();
      }
    }
  };
  // Native iOS back button closed the MeiQia chat cover without tapping MeiQia's own
  // close button. MeiQia's hidePanel callback never fires in that case, so the panel
  // DOM stays open and floats above the next page. This handler closes it explicitly.
  const handleMeiqiaPanelClosed = () => {
    // @ts-ignore
    if (typeof _MEIQIA === "function") {
      // @ts-ignore
      _MEIQIA("hidePanel");
    }
  };
  useEffect(() => {
    initWebViewEventListener();

    const unsubscribeFcmTokenUpdated = subscribeToWebViewEvent(
      WEB_VIEW_APP_EVENTS.FCM_TOKEN_UPDATED,
      fcmTokenUpdatedHandler,
    );

    const unsubscribeSingular = subscribeToWebViewEvent(
      WEB_VIEW_APP_EVENTS.SINGULAR_DEEPLINK,
      singularDeeplinkHandler,
    );

    const unsubscribeBack = subscribeToWebViewEvent(
      WEB_VIEW_APP_EVENTS.BACK_ACTION_TRIGGERED,
      backActionHandler,
    );

    const unsubscribeUserAuth = subscribeToWebViewEvent(
      WEB_VIEW_APP_EVENTS.USER_AUTH_TOKEN,
      userAuthHandler,
    );
    const unsubscribeUserLoggedOut = subscribeToWebViewEvent(
      WEB_VIEW_APP_EVENTS.USER_LOGGED_OUT,
      userLoggedOutHandler,
    );
    const unsubscribeZendeskWidget = subscribeToWebViewEvent(
      WEB_VIEW_APP_EVENTS.ZENDESK_TOGGLE,
      handleZendeskWidget,
    );

    const unsubscribeOpenAIChat = subscribeToWebViewEvent(
      WEB_VIEW_APP_EVENTS.OPEN_AI_CHAT,
      openAIChatHandler,
    );

    const unsubscribeNavigateToRoute = subscribeToWebViewEvent(
      WEB_VIEW_APP_EVENTS.NAVIGATE_TO_ROUTE,
      navigateToRouteHandler,
    );
    const unsubscribeMeiqiaPanelClosed = subscribeToWebViewEvent(
      WEB_VIEW_APP_EVENTS.MEIQIA_PANEL_CLOSED,
      handleMeiqiaPanelClosed,
    );

    return () => {
      unsubscribeSingular();
      unsubscribeBack();
      unsubscribeUserAuth();
      unsubscribeUserLoggedOut();
      unsubscribeZendeskWidget();
      unsubscribeFcmTokenUpdated();
      unsubscribeOpenAIChat();
      unsubscribeNavigateToRoute();
      unsubscribeMeiqiaPanelClosed();
    };
  }, []);
}

export default useWebViewEvents;
